import { useEffect, useRef, useCallback } from 'react'
import { trackEvent } from '@/shared/api/telemetry'
import type { View } from '@/shared/routes'

// Time-on-tab tracking. Accumulates the active (tab-visible) time spent in the
// current view and flushes it as a `view_dwell` event {view, ms} on: a view
// switch, the tab going hidden, every 30s of continuous use, and page unload
// (keepalive). Hidden time is never counted — the timer resets on re-show.
//
// Dwell alone cannot be read as a path: forty minutes in Focus and ten in Table
// says nothing about whether Table was opened once or twenty times, in which
// order, or what the person was looking at when they gave up. `view_nav`
// records each transition and `view_exit` records the last view standing when
// the page goes away, so the two together reconstruct the journey.
const FLUSH_MS = 30_000
const MAX_SEGMENT_MS = 60 * 60 * 1000 // guard against a stale/huge segment

export function useViewTracking(mode: View): void {
  const viewRef = useRef<View>(mode)
  const startRef = useRef<number>(Date.now())
  // Whether the segment currently accruing is tab-visible time. This is what
  // decides whether a flush emits — not the visibility state at flush time,
  // which is already 'hidden' by the time the hide/pagehide handlers run and
  // would drop exactly the segments those handlers exist to bank.
  const countingRef = useRef<boolean>(document.visibilityState === 'visible')

  const flush = useCallback(() => {
    const now = Date.now()
    const ms = now - startRef.current
    startRef.current = now
    if (countingRef.current && ms >= 1000 && ms < MAX_SEGMENT_MS) {
      trackEvent('view_dwell', { view: viewRef.current, ms })
    }
  }, [])

  // Fires once, on the last view the person was looking at before the page went
  // away. `pagehide` can be delivered more than once per document, and a second
  // exit would invent a view they never left.
  const exitedRef = useRef(false)
  const onPageHide = useCallback(() => {
    flush()
    if (exitedRef.current) return
    exitedRef.current = true
    trackEvent('view_exit', { view: viewRef.current })
  }, [flush])

  // View switch: bank the time on the old view, restart the clock on the new one.
  // No 1s floor on the transition itself — a view opened and abandoned inside a
  // second is the abandonment signal, not noise to be filtered out. Volume stays
  // bounded because a transition needs a deliberate click.
  useEffect(() => {
    if (viewRef.current !== mode) {
      const from = viewRef.current
      const ms = Date.now() - startRef.current
      flush()
      viewRef.current = mode
      startRef.current = Date.now()
      trackEvent('view_nav', { from, to: mode, ms: ms < MAX_SEGMENT_MS ? ms : undefined })
    }
  }, [mode, flush])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flush()
        countingRef.current = false
      } else {
        countingRef.current = true
        startRef.current = Date.now() // resume timing fresh
      }
    }
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') flush()
    }, FLUSH_MS)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      flush()
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [flush, onPageHide])
}
